import React, { useEffect, useState } from 'react';
import { Sparkles, TrendingUp, Ghost, Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

const RANGES = [
  { id: '7d',  label: '7d'  },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
];

const SOURCE_BADGE = {
  stock:    'bg-slate-700 text-slate-100',
  personal: 'bg-sky-900/60 text-sky-200',
  team:     'bg-purple-900/60 text-purple-200',
  deleted:  'bg-rose-900/60 text-rose-200',
};

const TemplateAnalyticsPage = () => {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/admin/template-analytics?range=${range}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error('Failed to load template analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const zombies = data?.zombie_templates || [];
  const top10 = rows.slice(0, 10);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-template-analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-red-400" />
            Template Analytics
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Which signal templates users actually convert on. Helps curate the stock catalog.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              data-testid={`template-analytics-range-${r.id}`}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                range === r.id
                  ? 'border-red-500/60 bg-red-950/30 text-red-200'
                  : 'border-slate-700 text-slate-400 hover:text-slate-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Headline KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Signals from templates', value: totals.signals_from_templates ?? 0 },
              { label: 'Conversions',             value: totals.conversions ?? 0 },
              { label: 'Overall conv rate',       value: `${totals.overall_conversion_rate ?? 0}%` },
              { label: 'Template share',          value: `${totals.template_share_pct ?? 0}%` },
              { label: 'Zombie templates',        value: totals.zombie_count ?? 0 },
            ].map((k) => (
              <Card key={k.label} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
                  <div className="text-xl font-semibold text-slate-100 mt-1">{k.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Top-10 by conversion */}
          <Card className="mb-6 bg-gray-800 border-gray-700" data-testid="template-analytics-top10">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                Top 10 by conversion rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              {top10.length === 0 ? (
                <div className="text-slate-500 text-sm py-6 text-center">
                  No templates have been used yet in this window.
                </div>
              ) : (
                <div style={{ width: '100%', height: 260, minHeight: 220 }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                    <BarChart
                      data={top10.map((r) => ({
                        name: r.template_name,
                        rate: r.conversion_rate,
                        uses: r.uses,
                      }))}
                      layout="vertical"
                      margin={{ top: 6, right: 20, left: 20, bottom: 6 }}
                    >
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} unit="%" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={140} />
                      <Tooltip
                        contentStyle={{ background: '#0F1730', border: '1px solid #334155' }}
                        formatter={(v, k) => (k === 'rate' ? [`${v}%`, 'Conv rate'] : [v, k])}
                      />
                      <Bar dataKey="rate" radius={[0, 4, 4, 0]}>
                        {top10.map((r) => (
                          <Cell
                            key={r.template_id}
                            fill={r.conversion_rate >= 60 ? '#10b981' : r.conversion_rate >= 30 ? '#f59e0b' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Full table */}
          <Card className="mb-6 bg-gray-800 border-gray-700" data-testid="template-analytics-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-base">Full ranking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-700">
                    <tr>
                      <th className="py-2 text-left">Template</th>
                      <th className="py-2 text-left">Category</th>
                      <th className="py-2 text-left">Source</th>
                      <th className="py-2 text-right">Uses</th>
                      <th className="py-2 text-right">Conversions</th>
                      <th className="py-2 text-right">Conv rate</th>
                      <th className="py-2 text-right">Active</th>
                      <th className="py-2 text-left">Last used</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-6 text-center text-slate-500">
                          No template usage in this window.
                        </td>
                      </tr>
                    ) : rows.map((r) => (
                      <tr
                        key={r.template_id}
                        className="hover:bg-slate-800/50"
                        data-testid={`template-analytics-row-${r.template_id}`}
                      >
                        <td className="py-2 text-slate-100">
                          {r.template_name}
                          {r.popular && (
                            <Badge className="ml-2 text-[9px] uppercase bg-amber-950/40 border-amber-800/60 text-amber-200">
                              Popular
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 text-slate-300 capitalize">{r.category}</td>
                        <td className="py-2">
                          <Badge className={`text-[9px] uppercase ${SOURCE_BADGE[r.source] || SOURCE_BADGE.stock}`}>
                            {r.source}
                          </Badge>
                        </td>
                        <td className="py-2 text-right text-slate-300">{r.uses}</td>
                        <td className="py-2 text-right text-slate-300">{r.conversions}</td>
                        <td className="py-2 text-right">
                          <span className={
                            r.conversion_rate >= 60 ? 'text-emerald-300 font-medium'
                            : r.conversion_rate >= 30 ? 'text-amber-300 font-medium'
                            : 'text-rose-300 font-medium'
                          }>
                            {r.conversion_rate}%
                          </span>
                        </td>
                        <td className="py-2 text-right text-slate-300">{r.active_signals}</td>
                        <td className="py-2 text-slate-400 text-xs">
                          {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Zombies */}
          <Card className="bg-gray-800 border-gray-700" data-testid="template-analytics-zombies">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                <Ghost className="h-4 w-4 text-slate-400" />
                Zombie templates
                <Badge className="text-[9px] uppercase bg-slate-700 text-slate-100">
                  {zombies.length}
                </Badge>
              </CardTitle>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Templates with zero uses in this window — candidates for cleanup or curation.
              </p>
            </CardHeader>
            <CardContent>
              {zombies.length === 0 ? (
                <div className="text-slate-500 text-sm py-4 text-center">
                  Every template got at least one signal this window. 
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {zombies.map((z) => (
                    <div
                      key={z.template_id}
                      className="flex items-center gap-2 px-3 py-2 rounded bg-slate-900/50 border border-slate-700"
                      data-testid={`template-analytics-zombie-${z.template_id}`}
                    >
                      <span className="text-sm text-slate-200 truncate flex-1">{z.template_name}</span>
                      <Badge className={`text-[9px] uppercase ${SOURCE_BADGE[z.source] || SOURCE_BADGE.stock}`}>
                        {z.source}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TemplateAnalyticsPage;
